import axios from 'axios';
import { z } from 'zod';

import authService from '@/services/auth/auth';

import { apiRoutes } from './config';

export type PageType = 'RECRUITER' | 'CANDIDATE';

export const PriceSchema = z.object({
  order: z.number(),
  id: z.number(),
  isTrial: z.boolean(),
  interval: z.string(),
  name: z.string(),
  description: z.string(),
  amount: z.number().nullable(),
  currency: z.string(),
  discount: z.number().nullable(),
  features: z.array(z.string()).nullable().default([]),
});
export type Price = z.infer<typeof PriceSchema> & {
  waitlist?: {
    buttonText: string;
    waitlistName: string;
  };
};

const PackagesSchema = z.object({
  prices: z.array(z.number()),
  name: z.string(),
  active: z.boolean(),
  description: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  type: z.enum(['CANDIDATE', 'RECRUITER']),
});

const redirectResponse = z.object({ url: z.string() });

export const getCustomerPortal = async (callbackUrl?: string) => {
  const { data } = await authService.get(
    apiRoutes.payments.stripe.customerPortal,
    {
      params: callbackUrl ? { callback_url: callbackUrl } : {},
    }
  );
  const validatedData = redirectResponse.parse(data);
  window.location.href = validatedData.url;
  return validatedData;
};

export const getProducts = async () => {
  const { data } = await axios.get(apiRoutes.payments.stripe.prices);
  return z.array(PriceSchema).parse(data);
};

export const postCheckout = async ({
  id,
  callbackUrl,
}: {
  id: number;
  callbackUrl?: string;
}) => {
  const { data } = await authService.post(
    apiRoutes.payments.stripe.checkout(id),
    {},
    {
      params: callbackUrl ? { callback_url: callbackUrl } : {},
    }
  );
  const validatedData = redirectResponse.parse(data);
  window.location.href = validatedData.url;
};

export const getPackages = async () => {
  const { data } = await axios.get(apiRoutes.payments.stripe.packages, {
    params: { offset: 0, limit: 999 },
  });
  return z.array(PackagesSchema).parse(data);
};

export const getUserCurrentSubscriptionData = async () => {
  const { data } = await authService.get(
    apiRoutes.payments.stripe.userCurrentBoughtPrice,
    {
      params: { offset: 0, limit: 999 },
    }
  );
  if (!data) return null;
  return PriceSchema.parse(data);
};

export const getTrialStatusData = async () => {
  const { data } = await authService.get(
    apiRoutes.payments.stripe.hasUserUsedTrial,
    { params: { offset: 0, limit: 999 } }
  );

  return z
    .object({
      wasTrialUsed: z.boolean(),
    })
    .parse(data);
};

export const getAvailablePricingForType = async (type: PageType) => {
  const { data } = await axios.get(apiRoutes.payments.stripe.pricingPlans, {
    params: { type },
  });
  return z.array(PriceSchema).parse(data);
};

const AccessCodeValidationSchema = z.object({
  valid: z.boolean(),
  message: z.string().optional(),
  expiresAt: z.string().nullable().optional(),
});
export type AccessCodeValidation = z.infer<typeof AccessCodeValidationSchema>;

const RedeemCodeResponseSchema = z.object({
  expiresAt: z.string().nullable().optional(),
  message: z.string().optional(),
});
export type RedeemCodeResponse = z.infer<typeof RedeemCodeResponseSchema>;

export const getValidateAccessCode = async (code: string) => {
  const { data } = await axios.get(
    apiRoutes.payments.stripe.validateAccessCode,
    {
      params: { code },
    }
  );
  return AccessCodeValidationSchema.parse(data);
};

export const postRedeemCode = async (code: string) => {
  const { data } = await authService.post(
    apiRoutes.payments.stripe.redeemCode,
    { code }
  );
  return RedeemCodeResponseSchema.parse(data);
};
